import React, { useState } from 'react';
import { Modal, Table, Button, Form, Input, Tag, Space, message, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { CirclePicker } from 'react-color';
import todoCategoryService from '../../../services/todoCategoryService';

const PICKER_COLORS = [
  '#1890ff', '#52c41a', '#fa541c', '#faad14', '#722ed1', '#13c2c2',
  '#eb2f96', '#fadb14', '#a0d911', '#fa8c16', '#eb2f96', '#2f54eb',
  '#000000', '#ffffff',
];

const PRESET_TO_HEX = {
  blue: '#1890ff', green: '#52c41a', red: '#ff4d4f', orange: '#fa8c16',
  purple: '#722ed1', cyan: '#13c2c2', pink: '#eb2f96', gold: '#faad14',
  lime: '#a0d911', volcano: '#fa541c', magenta: '#eb2f96', geekblue: '#2f54eb',
};

function toHex(color) {
  if (!color) return PICKER_COLORS[0];
  if (color.startsWith('#')) return color;
  return PRESET_TO_HEX[color] || PICKER_COLORS[0];
}

function ColorPickerField({ value, onChange }) {
  const hex = toHex(value);
  return (
    <CirclePicker
      color={hex}
      colors={PICKER_COLORS}
      onChangeComplete={(c) => onChange?.(c.hex)}
      circleSize={24}
    />
  );
}

const TodoCategoryManage = ({ visible, onClose, categories, onRefresh }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const handleOpenCreate = () => {
    setEditingId(null);
    form.resetFields();
    form.setFieldsValue({ color: PICKER_COLORS[0] });
    setModalOpen(true);
  };

  const handleOpenEdit = (record) => {
    setEditingId(record.id);
    form.setFieldsValue({
      name: record.name,
      color: record.color ? toHex(record.color) : PICKER_COLORS[0],
      sort_order: record.sort_order
    });
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        name: values.name,
        color: values.color || null,
        sort_order: values.sort_order !== undefined && values.sort_order !== '' ? Number(values.sort_order) : undefined,
      };
      setLoading(true);
      if (editingId) {
        await todoCategoryService.updateCategory(editingId, payload);
        message.success('Category updated');
      } else {
        await todoCategoryService.createCategory(payload);
        message.success('Category created');
      }
      setModalOpen(false);
      form.resetFields();
      onRefresh?.();
    } catch (err) {
      if (err.errorFields) return;
      message.error(err?.message || 'Failed to save category');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await todoCategoryService.deleteCategory(id);
      message.success('Category deleted');
      onRefresh?.();
    } catch (err) {
      message.error(err?.message || 'Failed to delete category');
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => (
        <Tag color={record.color || 'default'}>{name}</Tag>
      ),
    },
    {
      title: 'Color',
      dataIndex: 'color',
      key: 'color',
      render: (color) => color ? (
        <Space align="center">
          <span
            style={{
              display: 'inline-block',
              width: 16,
              height: 16,
              borderRadius: 4,
              backgroundColor: toHex(color),
              border: '1px solid rgba(0,0,0,0.1)',
            }}
          />
          {color.startsWith('#') ? color : <Tag color={color}>{color}</Tag>}
        </Space>
      ) : '—',
    },
    {
      title: 'Order',
      dataIndex: 'sort_order',
      key: 'sort_order',
      width: 80,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space>
          <Button type="text" size="small" icon={<EditOutlined />} onClick={() => handleOpenEdit(record)} />
          <Popconfirm
            title="Delete this category?"
            description="Todos in this category will become uncategorized."
            onConfirm={() => handleDelete(record.id)}
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Button type="text" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Modal
        title="Manage Todo Categories"
        open={visible}
        onCancel={onClose}
        footer={null}
        width={560}
        destroyOnClose
      >
        <p style={{ marginBottom: 16 }}>
          Create and edit categories to organize your todos. You can assign a category when adding or editing a todo.
        </p>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenCreate} style={{ marginBottom: 16 }}>
          Add Category
        </Button>
        <Table
          dataSource={categories}
          rowKey="id"
          columns={columns}
          pagination={false}
          size="small"
          locale={{ emptyText: 'No categories yet. Create one to get started.' }}
        />
      </Modal>

      <Modal
        title={editingId ? 'Edit Category' : 'Add Category'}
        open={modalOpen}
        onOk={handleSubmit}
        onCancel={() => { setModalOpen(false); form.resetFields(); }}
        confirmLoading={loading}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Enter category name' }, { max: 255, message: 'Max 255 characters' }]}
          >
            <Input placeholder="e.g. Work, Personal" />
          </Form.Item>
          <Form.Item
            name="color"
            label="Color"
          >
            <ColorPickerField />
          </Form.Item>
          <Form.Item
            name="sort_order"
            label="Sort order"
          >
            <Input type="number" min={0} placeholder="0" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default TodoCategoryManage;
